import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedPostData {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    avatarAlt: string;
  };
  publishDate: string;
  readTime: string;
  image: string;
  imageAlt: string;
}

interface FeaturedPostProps {
  post: FeaturedPostData;
}

const FeaturedPost = ({ post }: FeaturedPostProps) => {
  return (
    <section className="mb-16">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="StarIcon" size={24} className="text-accent" />
        <h2 className="text-2xl font-display font-bold text-foreground">Uitgelicht Artikel</h2>
      </div>
      
      <Link href={`/blog/${post.id}`} className="group block">
        <div className="glassmorphic rounded-3xl overflow-hidden hover:shadow-elevated transition-all duration-300">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="relative h-80 lg:h-auto overflow-hidden">
              <AppImage
                src={post.image}
                alt={post.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background/60 to-transparent lg:hidden"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-accent text-accent-foreground shadow-premium">
                  {post.category}
                </span>
              </div>
            </div>
            
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h3 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                {post.title}
              </h3>
              
              <p className="text-lg text-muted-foreground mb-6 line-clamp-4">
                {post.excerpt}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden">
                    <AppImage
                      src={post.author.avatar}
                      alt={post.author.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{post.author.name}</p>
                    <p className="text-sm text-muted-foreground">{post.publishDate}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="ClockIcon" size={18} />
                  <span>{post.readTime}</span>
                </div>
              </div>
              
              <div className="mt-6">
                <span className="inline-flex items-center space-x-2 text-primary font-semibold group-hover:gap-3 transition-all duration-300">
                  <span>Lees meer</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default FeaturedPost;